<template>
	<div class="mb-10 w-full">
	  <nav class="mt-6 -mb-px border-b flex space-x-2" aria-label="Tabs">
		<button
			v-for="(tab, slug) in enabledTabs" :key="slug"
			@click="selectedTab = slug"
			class="py-2 px-3 border border-gray-200 border-b-0 flex items-center space-x-2 whitespace-nowrap rounded-t-lg text-sm"
			:class="{
			'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != selectedTab,
			'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white': slug == selectedTab
			}">

			<InfoIcon :identifier="`dashboard-${slug}`" />

			<span>{{ tab.label }}</span>
			<span
				v-if="tab.showCount"
				class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
				:class="{
					'bg-gray-200 text-gray-900': slug != selectedTab,
					'bg-blue-800 text-white': slug == selectedTab
				}"
				>
				{{ tab.count }}
			</span>
		</button>
	</nav>


      <div class="pl-5 mb-6 border-l">
        <div>
        	<div v-if="selectedTab == 'projects'">
        		<div v-if="projectData.length">
	        		<div class="pt-4">
			        	<div class="bg-brand text-white p-4 rounded-t-lg">
			        		<div class="flex items-center justify-center text-center leading-5 space-x-16">
			        			<div>
			        				<p class="text-xl lg:text-3xl font-semibold" v-text="projectData.length"></p>
			        				<p class="text-base font-bold">{{ projectData.length | pluralize('Project') }}</p>
			        			</div>
			        			<div>
			        				<p data-precision="0" :data-originalvalue="projectsTotalRemaining" class="is-currency text-xl lg:text-3xl font-semibold">{{ formatCurrency(projectsTotalRemaining, 0) }}</p>
			        				<p class="text-base font-bold">Total Remaining</p>
			        			</div>
			        			<div>
			        				<p class="text-xl lg:text-3xl font-semibold">{{ projectsTotalSqFt | number }}</p>
			        				<p class="text-base font-bold">Total Sq. Ft.</p>
			        			</div>
			        		</div>
			        	</div>
			        </div>
			       	<div>
								<project-cards
									:project-data="projectData"
									view="table"
									show-completed-projects
									center-cards
									:show-header="false"
									:show-filters="false"
								></project-cards>
							</div>
						</div>
						<div v-else>
							<div class="flex items-center justify-center"><p class="none-found">No Projects Found</p></div>
						</div>
					</div>

					<div v-if="selectedTab == 'sites'">
						<div v-if="sites.filtered.length">
		        	<div class="pt-4">
			        	<div class="bg-brand text-white p-4 rounded-t-lg">
			        		<div class="flex items-center justify-center text-center leading-5 space-x-16">
			        			<div>
			        				<p class="text-xl lg:text-3xl font-semibold" v-text="this.sites.filtered.length"></p>
			        				<p class="text-base font-bold">{{ sites.filtered.length | pluralize('Site') }}</p>
			        			</div>
			        			<div>
			        				<p data-precision="0" :data-originalvalue="sitesTotalRemaining" class="is-currency text-xl lg:text-3xl font-semibold">{{ formatCurrency(sitesTotalRemaining, 0) }}</p>
			        				<p class="text-base font-bold">Total Remaining</p>
			        			</div>
			        			<div>
			        				<p class="text-xl lg:text-3xl font-semibold">{{ sitesTotalSqFt | number }}</p>
			        				<p class="text-base font-bold">Total Sq. Ft.</p>
			        			</div>
			        		</div>
			        	</div>
			        	<div>
				        	<site-cards
				        		:site-data="siteData"
				        		:show-create-new="false"
				        		:per-page="100"
				        		view="table"
				        		:show-header="false"
				        		:show-filters="false"
				        		center-cards
				        	>
				        	</site-cards>
				        </div>
			        </div>
			      </div>
						<div v-else>
							<div class="flex items-center justify-center"><p class="none-found">No Sites Found</p></div>
						</div>
					</div>

					<div v-if="selectedTab == 'keyDates'">
	        	<div class="p-2 pt-6">
            	<key-dates :sites="siteData" show-site-data></key-dates>
						</div>
					</div>
        </div>
      </div>
	</div>
</template>

<script>
	import { mapState } from 'vuex';

	export default {
    props: {
      siteData: {
	      type: Array,
	      required: true
      },

      projectData: {
        type: Array,
        required: true
      },

      openTab: {
          type: String,
          default: 'projects'
      },
    },

		data() {
			return {
				selectedTab: this.openTab,
				view: 'table'
			}
		},

		computed: {
		    enabledTabs() {
		        // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
		        // v-for first so the list was already filtered. Same result, and
		        // it does not break when the directive priority reverses in Vue 3.
		        const source = this.tabs;
		        return Array.isArray(source)
		            ? source.filter(entry => entry.enabled)
		            : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
		    },

			...mapState(['sites']),

      tabs() {
        return {
          projects: {
            label: "Projects",
			iconIdentifier: "projects-icon",
            enabled: true,
            showCount: true,
            count: this.projectData.length || 0
          },

          sites: {
            label: "Sites",
			iconIdentifier: "sites-icon",
            enabled: true,
            showCount: true,
            count: this.siteData.length || 0
          },

          keyDates: {
            label: "Key Dates",
			iconIdentifier: "keydates-icon",
            enabled: true,
            showCount: false
          },
        }
      },

			// Kept in cents: this component renders through the global formatCurrency
			// mixin, which divides by 100.
			projectsTotalRemaining() {
				return _.sumBy(this.projectData, 'remaining_lease_cost');
			},

			projectsTotalSqFt() {
				return _.sumBy(this.projectData, 'required_sq_ft');
			},

			sitesTotalRemaining() {
				return _.sumBy(this.sites.filtered, 'remaining_lease_cost');
			},

			sitesTotalSqFt() {
				return _.sumBy(this.sites.filtered, 'size');
			},
		},
	}
</script>
